/**
 * Drillhole Log Panel — multi-track graphical log preview + downloads.
 *
 * Fetches the PNG log via authenticated fetch (Blob) so we can:
 *  - Show a fast preview `<img>` from the object URL.
 *  - Offer instant download of the same PNG (no re-request).
 *  - Offer a PDF download that hits `/log.pdf` for a print-quality copy.
 *
 * The hole ID dropdown is populated from the parent — the panel does not
 * duplicate the /drillholes list request.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, FileText, Route, RefreshCw, Loader2, Printer, Package, Sparkles } from "lucide-react";


export default function DrillholeLogPanel({
  projectId,
  holes = [],
  initialHoleId = null,
  open,
  onOpenChange,
}) {
  const [selected, setSelected] = useState(initialHoleId);
  const [cutoff, setCutoff] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [loading, setLoading] = useState(false);
  // Per-hole AI narrative (Feb 2026 · Session I-29)
  const [aiNarrative, setAiNarrative] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    if (open) setSelected(initialHoleId || holes[0]?.id || null);
  }, [open, initialHoleId, holes]);

  // Reset narrative when the selected hole changes so we don't show
  // yesterday's paragraph against today's log.
  useEffect(() => { setAiNarrative(null); setAiOpen(false); }, [selected]);

  const holeMeta = useMemo(
    () => holes.find((h) => h.id === selected || h.hole_id === selected),
    [holes, selected],
  );

  const fetchLog = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (cutoff !== "" && !Number.isNaN(Number(cutoff))) params.set("cutoff", cutoff);
      const url = `/projects/${projectId}/drillholes/${selected}/log.png?${params.toString()}`;
      const r = await api.get(url, { responseType: "blob" });
      const objUrl = URL.createObjectURL(r.data);
      setImgUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return objUrl;
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to render log");
    } finally { setLoading(false); }
  }, [projectId, selected, cutoff]);

  useEffect(() => {
    if (open && selected) fetchLog();
  }, [open, selected, fetchLog]);

  useEffect(() => () => { if (imgUrl) URL.revokeObjectURL(imgUrl); }, [imgUrl]);

  const downloadPng = useCallback(() => {
    if (!imgUrl) return;
    const a = document.createElement("a");
    a.href = imgUrl;
    a.download = `smin_log_${holeMeta?.hole_id || selected}.png`;
    a.click();
  }, [imgUrl, holeMeta, selected]);

  const downloadPdf = useCallback(async () => {
    if (!selected) return;
    try {
      const params = new URLSearchParams();
      if (cutoff !== "" && !Number.isNaN(Number(cutoff))) params.set("cutoff", cutoff);
      const r = await api.get(
        `/projects/${projectId}/drillholes/${selected}/log.pdf?${params.toString()}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smin_log_${holeMeta?.hole_id || selected}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF download failed");
    }
  }, [projectId, selected, cutoff, holeMeta]);

  const downloadLogBook = useCallback(async () => {
    if (!selected) return;
    try {
      const params = new URLSearchParams();
      if (cutoff !== "" && !Number.isNaN(Number(cutoff))) params.set("cutoff", cutoff);
      const r = await api.get(
        `/projects/${projectId}/drillholes/${selected}/log-book.pdf?${params.toString()}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `SRES_log_book_${holeMeta?.hole_id || selected}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Professional log-book PDF ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Log-book download failed");
    }
  }, [projectId, selected, cutoff, holeMeta]);

  const generateAiNarrative = useCallback(async (force = false) => {
    if (!selected) return;
    setAiLoading(true);
    setAiOpen(true);
    // Retry once on 502 — first Claude call from a cold worker sometimes
    // exceeds the ingress timeout. The AI cache means the retry lands fast.
    const attempt = async () => {
      const { data } = await api.get(
        `/projects/${projectId}/drillholes/${selected}/ai-narrative${force ? "?force=true" : ""}`,
        { timeout: 120_000 },
      );
      return data;
    };
    try {
      let data;
      try { data = await attempt(); }
      catch (err) {
        const status = err?.response?.status;
        if (status === 502 || status === 504 || !status) {
          // brief pause then a single retry
          await new Promise((r) => setTimeout(r, 1500));
          data = await attempt();
        } else {
          throw err;
        }
      }
      setAiNarrative(data);
      if (force) toast.success("Narrative regenerated");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "AI narrative failed";
      toast.error(String(msg).slice(0, 200));
    } finally { setAiLoading(false); }
  }, [projectId, selected]);

  // ---- Bulk export — every hole's log (PNG + PDF) in one ZIP ----
  const [bulkBusy, setBulkBusy] = useState(false);
  const downloadAllZip = useCallback(async () => {
    setBulkBusy(true);
    try {
      const r = await api.post(
        `/projects/${projectId}/drillholes/logs.zip`,
        {
          hole_ids: [],
          formats: ["png", "pdf"],
          cutoff: cutoff !== "" && !Number.isNaN(Number(cutoff)) ? Number(cutoff) : null,
        },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smin_all_logs_${holes.length}holes.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${holes.length} logs as ZIP`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk export failed");
    } finally { setBulkBusy(false); }
  }, [projectId, cutoff, holes.length]);

  // ---- V4.0 Master Spec §14 — Continuous log (ONE PDF for entire project) ----
  const [continuousBusy, setContinuousBusy] = useState(false);
  const downloadContinuousPdf = useCallback(async () => {
    setContinuousBusy(true);
    try {
      const r = await api.post(
        `/projects/${projectId}/drillholes/logs.pdf`,
        {
          hole_ids: [],
          cutoff: cutoff !== "" && !Number.isNaN(Number(cutoff)) ? Number(cutoff) : null,
          include_ai_summary: true,
        },
        { responseType: "blob" },
      );
      // Guard: some servers return 200 with an application/json blob when
      // the report itself failed. Sniff the mime type before download.
      const blob = r?.data;
      const mime = blob?.type || "";
      if (!blob || (!mime.includes("pdf") && !mime.includes("octet-stream"))) {
        let msg = "Continuous log export failed";
        try {
          const txt = await blob.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) msg = parsed.detail;
        } catch { /* keep default */ }
        throw new Error(msg);
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `smin_continuous_log_${holes.length}holes.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Continuous log generated — ${holes.length} holes in one PDF`);
    } catch (err) {
      // Error responses with responseType:"blob" carry the JSON error as a
      // Blob, so `err.response.data.detail` won't work — parse it manually.
      let detail = err?.message || "Continuous log export failed";
      const errBlob = err?.response?.data;
      if (errBlob && typeof errBlob.text === "function") {
        try {
          const txt = await errBlob.text();
          const parsed = JSON.parse(txt);
          if (parsed?.detail) detail = parsed.detail;
        } catch { /* keep the axios default */ }
      } else if (typeof errBlob === "string") {
        detail = errBlob;
      } else if (errBlob?.detail) {
        detail = errBlob.detail;
      }
      toast.error(detail);
    } finally {
      // ALWAYS reset — guarantees the button never gets stuck in a spinner
      // state no matter which branch throws above.
      setContinuousBusy(false);
    }
  }, [projectId, cutoff, holes.length]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="drillhole-log-panel"
        className="rounded-none border-border bg-card sm:max-w-[900px] max-h-[92vh] p-0 overflow-hidden flex flex-col"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-border">
          <DialogTitle className="font-heading font-black text-xl tracking-tight flex items-center gap-2">
            <Route className="h-5 w-5 text-primary" />
            Drillhole Log Generator
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Multi-track graphical log — Lithology · Assay · Deviation. Print-quality PNG/PDF.
          </DialogDescription>
        </DialogHeader>

        <div className="px-6 py-3 border-b border-border grid grid-cols-1 md:grid-cols-6 gap-3 items-end bg-background/40">
          <label className="md:col-span-3">
            <div className="smin-label mb-1">Drillhole</div>
            <select
              value={selected || ""}
              onChange={(e) => setSelected(e.target.value)}
              data-testid="drillhole-log-select"
              className="w-full rounded-none bg-input border border-border font-mono h-9 text-xs px-2"
            >
              <option value="" disabled>— select a hole —</option>
              {holes.map((h) => {
                const depth = h.collar?.depth?.toFixed?.(0) ?? "—";
                const assayCount = h.assays?.length ?? 0;
                const label = `${h.hole_id} · depth ${depth} m · ${assayCount} assays`;
                return <option key={h.id} value={h.id}>{label}</option>;
              })}
            </select>
          </label>
          <label className="md:col-span-2">
            <div className="smin-label mb-1">Cut-off grade (optional)</div>
            <Input
              type="number" step="0.01" value={cutoff}
              placeholder="e.g. 0.3"
              onChange={(e) => setCutoff(e.target.value)}
              data-testid="drillhole-log-cutoff"
              className="rounded-none bg-input h-9 font-mono text-xs"
            />
          </label>
          <Button
            onClick={fetchLog}
            disabled={loading || !selected}
            data-testid="drillhole-log-render"
            className="rounded-none font-heading h-9"
          >
            {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
            Render
          </Button>
        </div>

        <div className="flex-1 overflow-auto bg-neutral-900/40 p-4 flex items-start justify-center">
          {loading ? (
            <div className="text-muted-foreground font-mono text-sm flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Rendering log…
            </div>
          ) : imgUrl ? (
            <img src={imgUrl}
                 alt={`Drillhole log ${holeMeta?.hole_id ?? ""}`}
                 data-testid="drillhole-log-image"
                 className="max-w-full shadow-lg border border-border/60 bg-white" />
          ) : (
            <div className="text-muted-foreground font-mono text-xs italic p-12 text-center">
              Select a hole and click Render.
            </div>
          )}
        </div>

        {/* AI Narrative — per-hole interpretation from Claude Sonnet 4.5 */}
        {selected && (
          <div className="border-t border-border px-6 py-3 bg-lime-600/[0.03]"
               data-testid="drillhole-ai-narrative-section">
            {!aiOpen ? (
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11.5px] text-muted-foreground flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-lime-500" />
                  Get an AI-authored interpretation for this hole — geology,
                  mineralisation, and QA/QC notes.
                </div>
                <Button variant="outline" size="sm"
                  onClick={() => generateAiNarrative(false)}
                  data-testid="drillhole-ai-analyze-btn"
                  className="rounded-none font-heading border-lime-600/50 text-lime-500 hover:bg-lime-600/10">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" /> AI Analyze
                </Button>
              </div>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-lime-500 flex items-center gap-2">
                    <Sparkles className="h-3.5 w-3.5" /> AI Interpretation
                    {aiNarrative?.cached && (
                      <span className="text-[10px] text-neutral-500 normal-case tracking-normal">
                        · cached · {aiNarrative.generated_at?.slice(0, 10)}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => generateAiNarrative(true)}
                    disabled={aiLoading}
                    data-testid="drillhole-ai-regenerate-btn"
                    className="text-[11px] text-lime-500 hover:text-lime-300 inline-flex items-center gap-1.5 disabled:opacity-50"
                    title="Force re-run — bypasses cache"
                  >
                    {aiLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    Regenerate
                  </button>
                </div>
                {aiLoading && !aiNarrative ? (
                  <div className="text-[12px] text-muted-foreground flex items-center gap-2 py-2">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Claude Sonnet 4.5 is analysing the hole…
                  </div>
                ) : aiNarrative ? (
                  <div className="text-[12.5px] leading-relaxed text-neutral-200 whitespace-pre-wrap"
                       data-testid="drillhole-ai-narrative-body">
                    {aiNarrative.narrative}
                  </div>
                ) : null}
              </div>
            )}
          </div>
        )}

        <div className="border-t border-border px-6 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="text-xs text-muted-foreground font-mono">
            {holeMeta ? (
              <>Hole <span className="text-primary">{holeMeta.hole_id}</span>
                {" · "}Depth {holeMeta.collar?.depth?.toFixed?.(1) ?? "—"} m
                {" · "}Lithology {holeMeta.lithology?.length ?? 0}
                {" · "}Assays {holeMeta.assays?.length ?? 0}
              </>
            ) : "—"}
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm"
              onClick={downloadAllZip} disabled={bulkBusy || holes.length === 0}
              data-testid="drillhole-log-download-all-zip"
              title="Download every drillhole's log (PNG + PDF) in a single ZIP"
              className="rounded-none font-heading border-primary/40 text-primary hover:bg-primary/10">
              {bulkBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Package className="h-3.5 w-3.5 mr-1.5" />}
              All logs · ZIP
            </Button>
            <Button variant="outline" size="sm"
              onClick={downloadContinuousPdf} disabled={continuousBusy || holes.length === 0}
              data-testid="drillhole-log-download-continuous-pdf"
              title="Master Spec §14 — ONE continuous PDF with cover, TOC, index, statistics, AI summary + one page per hole"
              className="rounded-none font-heading border-primary text-primary hover:bg-primary/10">
              {continuousBusy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}
              Continuous · one PDF
            </Button>
            <Button variant="outline" size="sm"
              onClick={downloadPng} disabled={!imgUrl}
              data-testid="drillhole-log-download-png"
              className="rounded-none font-heading">
              <Download className="h-3.5 w-3.5 mr-1.5" /> PNG
            </Button>
            <Button variant="outline" size="sm"
              onClick={downloadPdf} disabled={!selected}
              data-testid="drillhole-log-download-pdf"
              className="rounded-none font-heading">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> PDF
            </Button>
            <Button variant="default" size="sm"
              onClick={downloadLogBook} disabled={!selected}
              data-testid="drillhole-log-download-book"
              title="Professional 2-page SRES branded Drillhole Log Book (Sprint 102)"
              className="rounded-none font-heading bg-primary text-primary-foreground hover:bg-primary/90">
              <FileText className="h-3.5 w-3.5 mr-1.5" /> Log Book
            </Button>
            <Button variant="outline" size="sm"
              onClick={() => imgUrl && window.open(imgUrl, "_blank")}
              disabled={!imgUrl}
              data-testid="drillhole-log-open-new-tab"
              className="rounded-none font-heading">
              <Printer className="h-3.5 w-3.5 mr-1.5" /> Print View
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
